import * as React from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import Dropzone, { DropzoneRef, DropzoneProps } from 'react-dropzone';
import { Button, Typography, FormHelperText } from '@mui/material';

type ControlledImageSelectorProps = Omit<DropzoneProps, 'onDropAccepted' | 'onDropRejected'> & {
    name : string;
    control : Control<any, any>;
    label ?: string;
    defaultValue ?: string | null;
    onDropAccepted ?: (file : File, base64Image : string) => void;
    onDropRejected ?: (fileRejections : Array<File>) => void;
    className ?: string;
    buttonText ?: string;
    replaceButtonText ?: string;
    dropzoneText ?: string;
};

const ControlledImageSelector : React.FC<ControlledImageSelectorProps> = (props) => {
    const {
        name,
        control,
        label,
        defaultValue = null,
        onDropAccepted: onDropAcceptedProp,
        onDropRejected: onDropRejectedProp,
        className,
        buttonText = 'Select Image',
        replaceButtonText = 'Replace Image',
        dropzoneText = 'Drop image here, or click to select',
        accept = ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
        ...restDropzoneProps
    } = props;

    const dropzoneRef = React.useRef<DropzoneRef>(null);

    const openImageDialog = (event : React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field, fieldState: { error } }) => {
                const handleDropAccepted = (acceptedFiles : Array<File>) => {
                    if (acceptedFiles && acceptedFiles.length > 0) {
                        const file = acceptedFiles[0];
                        const reader = new FileReader();
                        reader.onload = () => {
                            const base64Image = reader.result as string;
                            field.onChange(base64Image);
                            if (onDropAcceptedProp) {
                                onDropAcceptedProp(file, base64Image);
                            }
                        };
                        reader.readAsDataURL(file);
                    }
                };

                const handleDropRejected = (fileRejections : Array<File>) => {
                    if (onDropRejectedProp) {
                        onDropRejectedProp(fileRejections);
                    }
                    // eslint-disable-next-line no-console
                    console.error('File rejected:', fileRejections);
                };

                return (
                    <div className={`fdc ${className || ''}`}>
                        {label && <Typography variant='caption' display='block' gutterBottom sx={{ marginLeft: '10px', color: error ? 'error.main' : 'text.secondary' }}>{label}</Typography>}
                        <Dropzone
                            {...restDropzoneProps}
                            accept={accept}
                            ref={dropzoneRef}
                            onDropAccepted={handleDropAccepted}
                            onDropRejected={handleDropRejected}
                            noKeyboard
                            multiple={false}
                        >
                            {({ getRootProps, getInputProps, isDragActive }) => (
                                <div
                                    {...getRootProps({
                                        className: `bcg1 PaperBorder p10 h300 w400 asc fdc aic jcc oln ${isDragActive ? 'dropzone-active' : ''} ${error ? 'dropzone-error' : ''}`,
                                        style: {
                                            borderStyle: 'dashed',
                                            borderWidth: '2px',
                                            borderColor: error ? 'red' : (isDragActive ? 'green' : 'rgba(0, 0, 0, 0.23)'),
                                            borderRadius: '4px',
                                            outline: 'none',
                                            cursor: 'pointer',
                                            transition: 'border .24s ease-in-out',
                                        },
                                    })}
                                >
                                    <input {...getInputProps({ className: 'oln' })} />
                                    <div className={'flx1'} />
                                    {field.value ? (
                                        <div className={'h200'}>
                                            <img className={'hi wi'} src={field.value as string} alt={label || 'Selected image'} style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain' }} />
                                        </div>
                                    ) : (
                                        <Typography variant='body1' color='textSecondary'>
                                            {isDragActive ? 'Drop the image here ...' : dropzoneText}
                                        </Typography>
                                    )}
                                    <div className={'flx1'} />
                                    <div className={'fdr'}>
                                        <Button
                                            aria-hidden
                                            tabIndex={-1}
                                            variant='contained'
                                            color='primary'
                                            onClick={openImageDialog}
                                            sx={{ mt: 1 }}
                                        >
                                            {field.value ? replaceButtonText : buttonText}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Dropzone>
                        {error && (
                            <FormHelperText error sx={{ marginLeft: '14px', marginRight: '14px' }}>
                                {(error as FieldError).message}
                            </FormHelperText>
                        )}
                    </div>
                );
            }}
        />
    );
};

export default ControlledImageSelector;
